/**
 * L'appel du matin sur le téléphone de l'animateur : `/presences/:jeton/*`.
 *
 * Sans compte. Le jeton de l'adresse vaut pour un centre et une semaine
 * (migration `20261024230000_les_liens_de_presences`) ; la base vérifie le
 * lien et le code à chaque geste. Cette page tient le code à quatre chiffres
 * et l'adresse (les tuiles, `g/<groupe>`, `e/<inscription>`) ; les écrans
 * eux-mêmes sont ceux de `components/presences/EcransDesPresences.tsx`, que
 * la coordination partage.
 *
 * Hors de toute mise en page : ni menu ni barre, l'écran d'un téléphone est
 * petit et l'animateur a un enfant devant lui.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LockKeyhole } from 'lucide-react';
import { EcransDesPresences } from '../../components/presences/EcransDesPresences';
import { adresseDeLaVue, sourceDuLien, vueDepuisLAdresse, type Vue } from '../../lib/presences/source';

/* Le code se garde pour ce lien, le temps de la session du navigateur. */
const cleDuCode = (jeton: string) => `presences-code-${jeton}`;
const lireCode = (jeton: string): string | null => {
  try {
    return sessionStorage.getItem(cleDuCode(jeton));
  } catch {
    return null;
  }
};
const garderCode = (jeton: string, code: string | null) => {
  try {
    if (code) sessionStorage.setItem(cleDuCode(jeton), code);
    else sessionStorage.removeItem(cleDuCode(jeton));
  } catch {
    /* navigation privée : on redemandera le code */
  }
};

export default function PresencesLienPage() {
  const params = useParams<{ jeton: string; '*': string }>();
  const jeton = params.jeton ?? '';
  const vue = vueDepuisLAdresse(params['*']);
  const navigate = useNavigate();
  const racine = `/presences/${jeton}`;

  const [code, setCode] = useState<string | null>(() => lireCode(jeton));
  const [codeRequis, setCodeRequis] = useState(false);
  const [codeFaux, setCodeFaux] = useState(false);

  const source = useMemo(() => sourceDuLien(jeton, code), [jeton, code]);
  const lienVers = useCallback((v: Vue) => adresseDeLaVue(racine, v), [racine]);
  const naviguer = useCallback((v: Vue) => navigate(adresseDeLaVue(racine, v)), [navigate, racine]);

  if (codeRequis) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <EcranDuCode
          codeFaux={codeFaux}
          onCode={(c) => {
            garderCode(jeton, c);
            setCode(c);
            setCodeRequis(false);
          }}
        />
      </div>
    );
  }

  return (
    <EcransDesPresences
      key={code ?? ''}
      source={source}
      vue={vue}
      lienVers={lienVers}
      naviguer={naviguer}
      onCodeRequis={() => {
        // Code manquant : on le demande. Code refusé : on le dit, et on l'oublie
        // pour la prochaine ouverture ; la source, elle, ne change pas tant que
        // l'écran du code est devant — changer de source ici relirait pour rien.
        setCodeFaux(code !== null);
        garderCode(jeton, null);
        setCodeRequis(true);
      }}
    />
  );
}

function EcranDuCode({ codeFaux, onCode }: { codeFaux: boolean; onCode: (code: string) => void }) {
  const [codeTape, setCodeTape] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (codeTape.length === 4) {
          onCode(codeTape);
          setCodeTape('');
        }
      }}
      className="mx-4 mt-16 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200"
    >
      <LockKeyhole className="mx-auto h-10 w-10 text-primary-500" />
      <h1 className="mt-3 text-center text-lg font-bold text-gray-900">Code du centre</h1>
      <p className="mt-1 text-center text-sm text-gray-600">Les quatre chiffres donnés par la coordination.</p>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={4}
        value={codeTape}
        onChange={(e) => setCodeTape(e.target.value.replace(/\D/g, '').slice(0, 4))}
        autoFocus
        className="mt-5 w-full rounded-xl border border-gray-300 px-4 py-4 text-center text-3xl tracking-[0.5em] focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
        aria-label="Code à quatre chiffres"
      />
      {codeFaux && <p className="mt-2 text-center text-sm text-red-600">Ce code n’est pas le bon.</p>}
      <button
        type="submit"
        disabled={codeTape.length !== 4}
        className="mt-4 w-full rounded-xl bg-primary-600 px-4 py-4 text-lg font-semibold text-white disabled:opacity-40"
      >
        Ouvrir la liste
      </button>
    </form>
  );
}
